import { useRouteMatch } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import {
  isUsingTiptapEditor,
  isUsingTrixEditor,
} from "@circle-react/helpers/postHelpers";
import { DisplayTrixContent } from "@circle-react-uikit/DisplayTrixContent";
import { TipTapBlockEditor } from "@circle-react-uikit/TipTapBlockEditor";
import { Typography } from "@circle-react-uikit/Typography";
import { CIRCLE_COMMUNITY_WHATS_NEW_URL } from "../constants";
import { useShowNews } from "../hooks/useShowNews";
import { HelpWidgetLayout } from "./common/HelpWidgetLayout";
import { SkeletonLoader } from "./common/SkeletonLoader";

interface NewsRouteParams {
  id: string;
}

export const NewsView = () => {
  const match = useRouteMatch<NewsRouteParams>();
  const id = match.params.id;

  const { isError, isLoading, data: post } = useShowNews(id);

  if (isLoading) {
    return (
      <HelpWidgetLayout className="flex flex-col gap-4">
        <SkeletonLoader
          rows={1}
          height="112px"
          wrapperClassName="mt-4 space-y-0"
          loaderClassName="rounded-2xl"
        />
        <SkeletonLoader rows={1} height="26px" loaderClassName="rounded-2xl" />
        <SkeletonLoader
          rows={1}
          width="310px"
          height="26px"
          loaderClassName="rounded-2xl"
        />
      </HelpWidgetLayout>
    );
  }

  if (isError) {
    return (
      <HelpWidgetLayout className="scrollbar-hide flex flex-col overflow-auto">
        <div className="support-widget-post__body grow">
          <div className="mx-6 my-3 max-h-full">
            {t("support_widget.post_not_found")}
          </div>
        </div>
      </HelpWidgetLayout>
    );
  }

  const tiptapBody = {
    ...post.tiptap_body,
    body: post.tiptap_inner_body_without_cta,
  };

  return (
    <HelpWidgetLayout className="scrollbar-hide flex flex-col overflow-auto">
      <div className="support-widget-post__body !mb-0 grow">
        {post.cover_image_url && (
          <div>
            <img loading="lazy" alt="" src={post.cover_image_url} />
          </div>
        )}
        <div className="mx-6 my-4">
          <Typography.TitleMd>
            <a
              className="!text-dark transition-colors duration-150 ease-linear hover:!text-[#4262F0]"
              href={post.post_url || CIRCLE_COMMUNITY_WHATS_NEW_URL}
              target="_blank"
              rel="noreferrer noopener"
            >
              {post.name}
            </a>
          </Typography.TitleMd>
        </div>
        <div
          id="news_view_post_body"
          className="mx-6 my-3 flex max-h-full flex-col gap-2"
        >
          {isUsingTrixEditor(post) && post.body_trix_content && (
            <DisplayTrixContent content={post.body_trix_content} />
          )}
          {isUsingTiptapEditor(post) && post.tiptap_body && (
            <TipTapBlockEditor
              readOnly
              value={tiptapBody.body}
              sgidToObjectMap={tiptapBody.sgids_to_object_map}
              inlineAttachments={tiptapBody.inline_attachments}
            />
          )}
          <div className="h-5 shrink-0" />
        </div>
      </div>
    </HelpWidgetLayout>
  );
};
